import type { ProjectHoursResponse } from "../api/types";
import type { PmProject, PmWorkItem } from "../api/pm";
import { MONTH_NAMES_FR, codeLabel, pad } from "./format";

/** Une journée facturable compte 7 heures. */
export const HOURS_PER_DAY = 7;

/** Mois civil, `month` de 0 (janvier) à 11. */
export interface Month {
  year: number;
  month: number;
}

/** Fuseau du tenant quand les réglages ne l'ont pas encore donné (défaut TAWA). */
export const DEFAULT_TIMEZONE = "Europe/Paris";

function zonedParts(
  d: Date,
  timeZone: string,
): { year: number; month: number; day: number } {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
    }).formatToParts(d);
  } catch {
    return zonedParts(d, DEFAULT_TIMEZONE);
  }
  const part = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value);
  return { year: part("year"), month: part("month") - 1, day: part("day") };
}

/** Mois civil de l'instant `d` dans le fuseau du tenant, pas celui du
 * navigateur : le découpage des jours est celui de TAWA. */
export function monthOf(d: Date, timeZone = DEFAULT_TIMEZONE): Month {
  const { year, month } = zonedParts(d, timeZone);
  return { year, month };
}

/** Jour « YYYY-MM-DD » de l'instant `d` dans le fuseau du tenant. */
export function dayOf(d: Date, timeZone = DEFAULT_TIMEZONE): string {
  const { year, month, day } = zonedParts(d, timeZone);
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

export function shiftMonth(m: Month, delta: number): Month {
  const d = new Date(Date.UTC(m.year, m.month + delta, 1));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
}

export function sameMonth(a: Month, b: Month): boolean {
  return a.year === b.year && a.month === b.month;
}

/** Premier et dernier jour du mois, « YYYY-MM-DD », bornes incluses. */
export function monthBounds(m: Month): { from: string; to: string } {
  const last = new Date(Date.UTC(m.year, m.month + 1, 0)).getUTCDate();
  const prefix = `${m.year}-${pad(m.month + 1)}`;
  return { from: `${prefix}-01`, to: `${prefix}-${pad(last)}` };
}

/** « Octobre 2026 ». */
export function monthLabel(m: Month): string {
  const s = new Date(Date.UTC(m.year, m.month, 1)).toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Période lue, jours « YYYY-MM-DD » du fuseau du tenant, bornes incluses. */
export interface Period {
  from: string;
  to: string;
}

export type PeriodPreset =
  "mois" | "mois-dernier" | "annee" | "debut" | "personnalisee";

export const PERIOD_PRESETS: { key: PeriodPreset; label: string }[] = [
  { key: "mois", label: "Ce mois" },
  { key: "mois-dernier", label: "Mois dernier" },
  { key: "annee", label: "Cette année" },
  { key: "debut", label: "Depuis le début" },
  { key: "personnalisee", label: "Personnalisée" },
];

/** Plage la plus longue que TAWA rend en une lecture : `to` au plus 1095 jours
 * après `from`, trois années civiles. */
export const MAX_PERIOD_SPAN_DAYS = 1095;

/** Borne basse de « Depuis le début » : TAWA ne donne pas la date du premier
 * temps connu. */
export const HISTORY_START = "2024-01-01";

const DAY_MS = 86_400_000;

/** Jour UTC d'une clé « YYYY-MM-DD », ou null si elle n'est pas un jour réel. */
function dayMs(key: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]) - 1, Number(m[3])];
  const ms = Date.UTC(y, mo, d);
  const back = new Date(ms);
  return back.getUTCFullYear() === y &&
    back.getUTCMonth() === mo &&
    back.getUTCDate() === d
    ? ms
    : null;
}

function keyOf(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function monthOfKey(key: string): Month {
  return { year: Number(key.slice(0, 4)), month: Number(key.slice(5, 7)) - 1 };
}

/** Plage d'un préréglage, relative au jour `today` du tenant. « Ce mois » et
 * « Mois dernier » couvrent le mois entier ; les autres s'arrêtent à `today`. */
export function presetPeriod(
  preset: Exclude<PeriodPreset, "personnalisee">,
  today: string,
): Period {
  const month = monthOfKey(today);
  switch (preset) {
    case "mois":
      return monthBounds(month);
    case "mois-dernier":
      return monthBounds(shiftMonth(month, -1));
    case "annee":
      return { from: `${month.year}-01-01`, to: today };
    case "debut": {
      const floor = keyOf((dayMs(today) ?? 0) - MAX_PERIOD_SPAN_DAYS * DAY_MS);
      return { from: floor > HISTORY_START ? floor : HISTORY_START, to: today };
    }
  }
}

/** Raison du refus d'une plage saisie, ou null si TAWA peut la lire. */
export function periodError(from: string, to: string): string | null {
  const a = dayMs(from);
  const b = dayMs(to);
  if (a === null || b === null) return "Choisissez deux dates.";
  if (a > b) return "La date de début doit précéder la date de fin.";
  if ((b - a) / DAY_MS > MAX_PERIOD_SPAN_DAYS)
    return "Trois ans au plus : raccourcissez la période.";
  return null;
}

/** Le mois civil que couvre exactement la période, sinon null. */
export function periodMonth(p: Period): Month | null {
  const month = monthOfKey(p.from);
  const b = monthBounds(month);
  return b.from === p.from && b.to === p.to ? month : null;
}

const FIXED_PRESETS = ["mois-dernier", "annee", "debut"] as const;

/** Période portée par l'URL : `periode` (préréglage) ou `du` et `au`. Sans
 * paramètre, ou sur un lien invalide (`invalid`), le mois courant. */
export function readPeriod(
  params: URLSearchParams,
  today: string,
): { period: Period; preset: PeriodPreset; invalid: boolean } {
  const named = params.get("periode");
  const fixed = FIXED_PRESETS.find((p) => p === named);
  if (fixed)
    return {
      period: presetPeriod(fixed, today),
      preset: fixed,
      invalid: false,
    };
  const from = params.get("du");
  const to = params.get("au");
  const current = {
    period: presetPeriod("mois", today),
    preset: "mois" as const,
  };
  if (from === null && to === null && (named === null || named === "mois"))
    return { ...current, invalid: false };
  if (from === null || to === null || periodError(from, to))
    return { ...current, invalid: true };
  const period = { from, to };
  const same = (q: Period) => q.from === from && q.to === to;
  const preset =
    (["mois", "mois-dernier", "annee"] as const).find((p) =>
      same(presetPeriod(p, today)),
    ) ?? "personnalisee";
  return { period, preset, invalid: false };
}

/** Paramètres d'URL d'une période : rien pour le mois courant, le nom d'un
 * préréglage glissant, sinon les deux dates. */
export function periodParams(
  period: Period,
  today: string,
  preset?: PeriodPreset,
): Record<string, string> {
  if (preset && (FIXED_PRESETS as readonly string[]).includes(preset))
    return { periode: preset };
  const current = presetPeriod("mois", today);
  if (period.from === current.from && period.to === current.to) return {};
  return { du: period.from, au: period.to };
}

function shortDay(key: string, withYear: boolean): string {
  const m = monthOfKey(key);
  const day = Number(key.slice(8, 10));
  return `${day} ${MONTH_NAMES_FR[m.month] ?? ""}${withYear ? ` ${m.year}` : ""}`;
}

/** « Octobre 2026 » pour un mois entier, « 1 janv. au 21 oct. 2026 » sinon. */
export function periodLabel(p: Period): string {
  const month = periodMonth(p);
  if (month) return monthLabel(month);
  if (p.from === p.to) return shortDay(p.from, true);
  const sameYear = p.from.slice(0, 4) === p.to.slice(0, 4);
  return `${shortDay(p.from, !sameYear)} au ${shortDay(p.to, true)}`;
}

/** Complément de phrase : « en octobre 2026 », « du 1 janv. au 21 oct. 2026 ». */
export function periodPhrase(p: Period): string {
  const label = periodLabel(p);
  if (periodMonth(p)) return `en ${label.toLowerCase()}`;
  return p.from === p.to ? `le ${label}` : `du ${label}`;
}

const daysFormat = new Intl.NumberFormat("fr-FR", {
  maximumFractionDigits: 2,
});

/** Minutes en jours de HOURS_PER_DAY heures : « 1,5 j ». */
export function formatDays(minutes: number): string {
  return `${daysFormat.format(minutes / (HOURS_PER_DAY * 60))} j`;
}

/** Nom affichable d'une référence MDM (`mdm://party/<id>`) : son dernier
 * segment. MDM n'est pas exposé à Chronos par la passerelle, le nom du tiers
 * n'est donc pas résolu ; un identifiant opaque devient « Client <id court> »,
 * une référence libre en minuscules (« mega-verre ») est mise en forme. */
export function partyLabel(url: string): string {
  const raw =
    url
      .replace(/[/#?]+$/, "")
      .split(/[/:]/)
      .pop() ?? "";
  let tail = raw;
  try {
    tail = decodeURIComponent(raw);
  } catch {
    tail = raw;
  }
  if (!tail) return url;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(tail))
    return `Client ${tail.slice(0, 8)}`;
  if (/^\d+$/.test(tail)) return `Client ${tail}`;
  return tail === tail.toLowerCase() ? humanizeSlug(tail) : tail;
}

/** « yves-briend-editeur » devient « Yves Briend Editeur » : majuscule aux mots
 * de plus de deux lettres et au premier. */
function humanizeSlug(slug: string): string {
  return slug
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w, i) =>
      i === 0 || w.length > 2 ? w.charAt(0).toUpperCase() + w.slice(1) : w,
    )
    .join(" ");
}

export interface BillingWorkItem {
  key: string;
  label: string;
  minutes: number;
}

export interface BillingProject {
  id: string;
  label: string;
  minutes: number;
  workItems: BillingWorkItem[];
}

export interface BillingClient {
  key: string;
  label: string;
  minutes: number;
  projects: BillingProject[];
}

export const NO_CLIENT_KEY = "sans-client";
export const UNKNOWN_CLIENT_KEY = "client-inconnu";

/** Regroupe le temps TAWA par client (PM `client_party_url`), puis projet, puis
 * élément de travail. Un projet absent de PM tombe sous « Client inconnu », un
 * projet sans client sous « Sans client » ; ces deux groupes viennent en
 * dernier, les autres par temps décroissant. */
export function buildBillingTree(
  hours: ProjectHoursResponse,
  projects: PmProject[],
  workItems: Map<string, PmWorkItem[]>,
): BillingClient[] {
  const pmById = new Map(projects.map((p) => [p.id, p]));
  const clients = new Map<string, BillingClient>();
  for (const ph of hours.projects) {
    const pm = pmById.get(ph.project_id);
    const key = !pm
      ? UNKNOWN_CLIENT_KEY
      : (pm.client_party_url ?? "") || NO_CLIENT_KEY;
    const label =
      key === UNKNOWN_CLIENT_KEY
        ? "Client inconnu"
        : key === NO_CLIENT_KEY
          ? "Sans client"
          : partyLabel(key);
    let client = clients.get(key);
    if (!client) {
      client = { key, label, minutes: 0, projects: [] };
      clients.set(key, client);
    }
    const items = new Map(
      (workItems.get(ph.project_id) ?? []).map((w) => [w.id, w]),
    );
    client.minutes += ph.worked_minutes;
    client.projects.push({
      id: ph.project_id,
      label: pm
        ? codeLabel(pm.code, pm.name)
        : ph.project_label || `Projet ${ph.project_id.slice(0, 8)}`,
      minutes: ph.worked_minutes,
      workItems: ph.work_items.map((w) => ({
        key: w.work_item_id ?? "",
        label: w.work_item_id
          ? (items.get(w.work_item_id)?.title ??
            `Élément ${w.work_item_id.slice(0, 8)}`)
          : "Sans élément de travail",
        minutes: w.worked_minutes,
      })),
    });
  }
  const last = (k: string) =>
    k === UNKNOWN_CLIENT_KEY ? 2 : k === NO_CLIENT_KEY ? 1 : 0;
  return [...clients.values()].sort(
    (a, b) =>
      last(a.key) - last(b.key) ||
      b.minutes - a.minutes ||
      a.label.localeCompare(b.label, "fr"),
  );
}
